import React, { useCallback, useEffect, useMemo } from 'react';
import { useStore } from 'zustand/react';
import { useShallow } from 'zustand/react/shallow';
import { useTranslations } from '@components/context';
import ModalComponent from '@components/ModalComponent';
import {
    selectConfigs,
    selectEditorLinks,
    selectAutomationModal,
    selectAutomationRuleForm,
    selectAutomationRuleSaving,
    selectResolvedSelectedAction,
    selectFetchingRuleId,
    selectActionResources,
} from '@stores/positionAutomation/PositionAutomation.selectors';
import { notification } from '@utils/utils';
import { AUTOMATION_CONTEXT_OUTCOME, automationCategoryFilterType } from '../helper';
import { mapSchedulingOptions } from '../schedulingHelpers';
import TimeBasedSchedulingFields from './TimeBasedSchedulingFields';
import AutomationConditionsEditor from './AutomationConditionsEditor';
import AutomationActionsEditor from './AutomationActionsEditor';
import Button from '@components/legacy/core/Button';
import '../../Automations/components/AddAutomationModal/AddAutomationModal.less';
import SelectDropdown from '@components/core/SelectDropdown';
import './AutomationRuleModal.less';

const filterEventsForCategory = (events, categoryType) => {
    if (!events || !categoryType) {
        return events || {};
    }
    return Object.fromEntries(
        Object.entries(events).filter(([, entry]) => {
            const when = entry?.show_when;
            if (!when || !when.length) {
                return true;
            }
            return when.includes(categoryType);
        }),
    );
};

const toEventSelectOptions = (events) =>
    Object.entries(events || {}).map(([id, entry]) => ({
        id,
        text: entry?.title ?? id,
    }));

const AutomationRuleModal = ({ store }) => {
    const { t } = useTranslations();
    const automationModal = useStore(store, selectAutomationModal);
    const show = Boolean(automationModal);
    const { category, contextId, selectedAction, ruleDetail, readOnly } = automationModal ?? {};
    const { data: configs, loading: configsLoading, error: configsError } = useStore(store, useShallow(selectConfigs));
    const links = useStore(store, selectEditorLinks);
    const fetchConfigs = useStore(store, (s) => s.fetchConfigs);
    const fetchActionResources = useStore(store, (s) => s.fetchActionResources);
    const actionResourcesState = useStore(store, useShallow(selectActionResources));
    const patchAutomationRuleForm = useStore(store, (s) => s.patchAutomationRuleForm);
    const patchActionValue = useStore(store, (s) => s.patchActionValue);
    const resetAutomationRuleForm = useStore(store, (s) => s.resetAutomationRuleForm);
    const saveAutomationRule = useStore(store, (s) => s.saveAutomationRule);
    const closeAutomationModal = useStore(store, (s) => s.closeAutomationModal);
    const automationRuleForm = useStore(store, selectAutomationRuleForm);
    const saving = useStore(store, selectAutomationRuleSaving);
    const resolvedSelectedAction = useStore(store, selectResolvedSelectedAction);
    const fetchingRuleId = useStore(store, selectFetchingRuleId);

    const { event, cron, groups, topOperator, actionValue } = automationRuleForm;

    const patchForm = useCallback((partial) => patchAutomationRuleForm(partial), [patchAutomationRuleForm]);


    const isEdit = Boolean(ruleDetail);
    const saveLink = isEdit ? ruleDetail?._links?.put : links?.post;
    const saveLinkPresent = Boolean(saveLink?.url);

    const handleSave = useCallback(async () => {
        const r = await saveAutomationRule();
        if (r.ok) {
            return;
        }
        if (r.validationKey) {
            notification(t(r.validationKey), { type: 'warning' });
            return;
        }
        if (r.errorMessage) {
            notification(r.errorMessage || t('Error occurred.'), { type: 'error' });
        }
    }, [saveAutomationRule, t]);

    useEffect(() => {
        if (show) {
            fetchConfigs({
                mode: category === AUTOMATION_CONTEXT_OUTCOME ? 'outcome' : 'step',
                notification: false,
            });
        }

        return () => {
            resetAutomationRuleForm();
        };
    }, [show, category, fetchConfigs, resetAutomationRuleForm]);

    useEffect(() => {
        if (!show || ruleDetail) {
            return;
        }
        if (selectedAction) {
            patchForm({ actionValue: {} });
        }
    }, [show, selectedAction, ruleDetail, patchForm]);

    useEffect(() => {
        store.getState().syncAutomationRuleFormFromRuleDetail();
    }, [store, show, ruleDetail, configs, configsLoading]);

    const resolvedActionId = ruleDetail?.action?.id ?? selectedAction?.id;
    const resourcesForCurrentAction =
        actionResourcesState.actionId === resolvedActionId ? actionResourcesState : null;

    useEffect(() => {
        if (!show || !resolvedActionId) {
            return undefined;
        }
        const controller = new AbortController();
        fetchActionResources(resolvedActionId, { signal: controller.signal, notification: false });
        return () => controller.abort();
    }, [show, resolvedActionId, fetchActionResources]);

    const categoryType = category ? automationCategoryFilterType(category) : null;

    const filteredEvents = useMemo(
        () => filterEventsForCategory(configs?.events, categoryType),
        [configs?.events, categoryType],
    );
    const eventOptions = useMemo(() => toEventSelectOptions(filteredEvents), [filteredEvents]);

    const schedulingDropdownOptions = useMemo(() => {
        const schedulingOptions = configs?.events?.time_based_event?.options;
        return schedulingOptions ? mapSchedulingOptions(schedulingOptions) : null;
    }, [configs?.events?.time_based_event?.options]);
    const scheduleFrequencies = configs?.events?.time_based_event?.options?.frequencies;

    const saveDisabled =
        saving ||
        readOnly ||
        !saveLinkPresent ||
        !resolvedSelectedAction ||
        configsLoading ||
        Boolean(configsError) ||
        !configs;

    const showSaveButton = !readOnly && !!saveLink?.url;

    return (
        <ModalComponent show={show} closeModal={closeAutomationModal}>
            <div className="overlay add-automation-modal automation-rule-modal">
                <div className="overlay-header">
                    <h3>{resolvedSelectedAction?.name}</h3>
                </div>
                <div className="overlay-main no-vertical-padding">
                    <div className="add-automation-modal__layout">
                        <aside className="add-automation-modal__sidebar">
                            <div className="add-automation-modal__sidebar-section">
                                <div className="add-automation-modal__sidebar-heading">
                                    {t('Trigger when…')}
                                </div>
                                <SelectDropdown
                                    id="automation-event"
                                    name="event"
                                    options={eventOptions}
                                    value={event ?? null}
                                    className="add-automation-modal__select2 add-automation-modal__select2--block add-automation-modal__select automation-field automation-field--event"
                                    placeholder={t('Trigger type')}
                                    onChange={(next) => patchForm({ event: next || null })}
                                    disabled={readOnly}
                                    searchable={false}
                                />
                            </div>
                            <div className="add-automation-modal__sidebar-section add-automation-modal__sidebar-section--scheduled">
                                <div className="auto-sched">
                                    {schedulingDropdownOptions ? (
                                        <TimeBasedSchedulingFields
                                            options={schedulingDropdownOptions}
                                            frequencies={scheduleFrequencies}
                                            visible={event === 'time_based_event'}
                                            onChange={(next) => patchForm({ cron: next })}
                                            value={cron}
                                            readOnly={readOnly}
                                        />
                                    ) : null}
                                    <AutomationConditionsEditor
                                        configs={configs}
                                        event={event}
                                        groups={groups}
                                        onChangeGroups={(next) => patchForm({ groups: next })}
                                        topOperator={topOperator}
                                        onTopOperatorChange={(next) => patchForm({ topOperator: next })}
                                        readOnly={readOnly}
                                    />

                                </div>
                            </div>
                        </aside>
                        <div className="add-automation-modal__body">
                            {configsLoading ? <p className="text-muted">{t('Loading configuration…')}</p> : null}
                            {configsError ? <p className="text-danger">{configsError}</p> : null}
                            {!configsLoading && !configsError && configs ? (
                                <>
                                    {resolvedSelectedAction && !resourcesForCurrentAction?.loading ? (
                                        <AutomationActionsEditor
                                            selectedAction={resolvedSelectedAction}
                                            category={category}
                                            contextId={contextId}
                                            actionValue={actionValue}
                                            onChangeActionValue={patchActionValue}
                                            readOnly={readOnly}
                                            actionResources={resourcesForCurrentAction?.data ?? null}
                                            actionResourcesError={resourcesForCurrentAction?.error ?? null}
                                        />
                                    ) : null}
                                </>
                            ) : null}
                        </div>
                    </div>
                </div>
                <div className="overlay-footer">
                    <div className="btn-container">
                        <Button type="link" title={t('Cancel')} onClick={closeAutomationModal} />
                        {showSaveButton && (
                            <Button
                                type="primary"
                                title={t('Save')}
                                disabled={saveDisabled}
                                onClick={handleSave}
                            />
                        )}
                    </div>
                </div>
            </div>
        </ModalComponent>
    );
};

export default AutomationRuleModal;
